import type { Href } from "expo-router";
import { router } from "expo-router";
import { useEffect, useState } from "react";
import { ActivityIndicator, Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";

import { COLORS, FONTS } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import { listMyCompetitions } from "../../../lib/clientCompetitions";
import { getMyCompanies, listCompanyMembers } from "../../../lib/companies";
import { markWelcomeSeen } from "../../../lib/clientWelcome";
import { useAuth } from "../../../lib/authContext";
import type { ClientCompetitionListRow, Company } from "../../../types/database";

const THEME = PANE_THEMES.Client;

const BLACK = THEME.pageBg;
const FOREST_MID = COLORS.FOREST_MID.hex;
const AMBER = THEME.accent;
const GOLD = COLORS.GOLD_RM.hex;
const SAGE = COLORS.SAGE.hex;
const LINEN = THEME.textOnSurface;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";

type AudienceState =
  | { kind: "rm-waiting"; firstName: string; companyName: string; rmProgram: ClientCompetitionListRow }
  | { kind: "licensing-only"; firstName: string }
  | { kind: "added-by-owner"; firstName: string; companyName: string; ownerName: string }
  | { kind: "fresh"; firstName: string }
  | { kind: "loading" };

function firstNameFromUser(meta: Record<string, unknown>, email: string | null | undefined): string {
  const full = typeof meta.full_name === "string" ? meta.full_name.trim() : "";
  if (full) return full.split(/\s+/)[0];
  if (email) return email.split("@")[0];
  return "there";
}

export default function ClientWelcomeScreen() {
  const { user } = useAuth();
  const [audience, setAudience] = useState<AudienceState>({ kind: "loading" });
  const [busyContinue, setBusyContinue] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
      const firstName = firstNameFromUser(meta, user?.email ?? null);

      const [companies, comps] = await Promise.all([getMyCompanies(), listMyCompetitions()]);
      if (cancelled) return;

      const company: Company | null = companies[0] ?? null;
      const companyName = company?.name ?? "your team";

      const rmProgram = comps.find((c) => c.engagement_model === "rm");
      const licensingProgram = comps.find((c) => c.engagement_model !== "rm");

      // Detect "added by owner" — there's an owner on this company OTHER
      // than the current user, and the current user is a member, not owner.
      let addedByOwnerName: string | null = null;
      if (company && user?.id) {
        const members = await listCompanyMembers(company.id);
        if (cancelled) return;
        const me = members.find((m) => m.user_id === user.id);
        const owner = members.find((m) => m.company_role === "owner" && m.user_id !== user.id);
        if (me?.company_role === "member" && owner?.full_name) {
          addedByOwnerName = owner.full_name;
        }
      }

      if (rmProgram) {
        setAudience({ kind: "rm-waiting", firstName, companyName, rmProgram });
        return;
      }
      if (addedByOwnerName) {
        setAudience({
          kind: "added-by-owner",
          firstName,
          companyName,
          ownerName: addedByOwnerName,
        });
        return;
      }
      if (licensingProgram) {
        setAudience({ kind: "licensing-only", firstName });
        return;
      }
      setAudience({ kind: "fresh", firstName });
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  async function onContinue() {
    if (busyContinue) return;
    setBusyContinue(true);
    await markWelcomeSeen();
    router.replace("/client" as Href);
  }

  if (audience.kind === "loading") {
    return (
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center", backgroundColor: BLACK }}>
        <ActivityIndicator size="small" color={AMBER} />
      </View>
    );
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: BLACK }} contentContainerStyle={{ paddingBottom: 100 }}>
      <View style={{ paddingHorizontal: 32, paddingVertical: 28, maxWidth: 980 }}>
        <Hero audience={audience} />
        <ProcessMap kind={audience.kind} />
        <WhatsNext audience={audience} />
        <Footer onContinue={() => void onContinue()} busy={busyContinue} />
      </View>
    </ScrollView>
  );
}

// ─── Hero ──────────────────────────────────────────────────────────────

type ResolvedAudience = Exclude<AudienceState, { kind: "loading" }>;

function Hero({ audience }: { audience: ResolvedAudience }) {
  let headline: React.ReactNode;
  if (audience.kind === "rm-waiting") {
    headline = (
      <>
        {`You're set up, ${audience.firstName}.\n`}
        <Text style={{ fontStyle: "italic", color: GOLD }}>
          {audience.companyName}
        </Text>
        {`'s first program is ready to review.`}
      </>
    );
  } else if (audience.kind === "added-by-owner") {
    headline = (
      <>
        {`You've been added to `}
        <Text style={{ fontStyle: "italic", color: AMBER }}>{audience.companyName}</Text>
        {`'s Coravu portal by ${audience.ownerName}.\nHere's a quick tour.`}
      </>
    );
  } else if (audience.kind === "licensing-only") {
    headline = (
      <>
        {`Welcome to Coravu, ${audience.firstName}.\n`}
        <Text style={{ fontStyle: "italic", color: AMBER }}>{"Here's how the platform works."}</Text>
      </>
    );
  } else {
    headline = (
      <>
        {`Welcome to Coravu, ${audience.firstName}.\n`}
        <Text style={{ fontStyle: "italic", color: AMBER }}>
          {"Here's how Coravu works"}
        </Text>
        {` — and how to start your first program.`}
      </>
    );
  }

  return (
    <View>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 9.5,
            letterSpacing: 2,
            textTransform: "uppercase",
            color: AMBER,
          }}
        >
          Welcome to Coravu
        </Text>
      </View>
      <Text
        style={{
          marginTop: 10,
          fontFamily: FONTS.display,
          fontSize: 38,
          lineHeight: 44,
          color: LINEN,
        }}
      >
        {headline}
      </Text>
      <Text
        style={{
          marginTop: 12,
          fontFamily: FONTS.bodyLight,
          fontSize: 14,
          lineHeight: 22,
          color: WS,
          maxWidth: 720,
        }}
      >
        Coravu runs end-to-end case competitions to surface real-world talent. Either you run the program yourself (Self-Managed) or we run it for you (Coravu Managed).
      </Text>
    </View>
  );
}

// ─── Process map ───────────────────────────────────────────────────────

type Phase = { n: string; title: string; body: string };

const PHASES_RM: Phase[] = [
  { n: "1", title: "Discovery", body: "Introductory call, 60 minutes virtual." },
  { n: "2", title: "Custom case design", body: "Coravu drafts the competition. No payment yet." },
  { n: "3", title: "Onboarding & contract", body: "We finalize the program and sign." },
  {
    n: "4",
    title: "Launch preparation",
    body: "Coravu locks in venue and student outreach (post-fee).",
  },
  { n: "5", title: "Work period", body: "Students get the brief and submit deliverables." },
  { n: "6", title: "Live monitoring", body: "Your portal is the command center for everything in flight." },
  { n: "7", title: "Presentation day", body: "Your team judges the finalists in person." },
];

const PHASES_LICENSING: Phase[] = [
  { n: "1", title: "Discovery", body: "Tell us what you want from the program." },
  { n: "2", title: "Brief design", body: "You write the brief, deliverables, and rubric in the editor." },
  { n: "3", title: "Onboarding & contract", body: "We finalize the program and sign." },
  { n: "4", title: "Launch", body: "Launch your competition once your brief is finalized." },
  { n: "5", title: "Work period", body: "Students get the brief and submit deliverables." },
  { n: "6", title: "Live monitoring", body: "Your portal is the command center for everything in flight." },
  { n: "7", title: "Judging & results", body: "Your team judges the finalists and announces results." },
];

function ProcessMap({ kind }: { kind: ResolvedAudience["kind"] }) {
  const phases = kind === "licensing-only" ? PHASES_LICENSING : PHASES_RM;
  return (
    <View style={{ marginTop: 36 }}>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 2,
          textTransform: "uppercase",
          color: AMBER,
        }}
      >
        How it works
      </Text>
      <Text
        style={{
          marginTop: 8,
          fontFamily: FONTS.display,
          fontSize: 28,
          lineHeight: 32,
          color: LINEN,
        }}
      >
        From first call to <Text style={{ fontStyle: "italic", color: AMBER }}>hired talent</Text>.
      </Text>

      <View style={{ marginTop: 22, position: "relative" }}>
        <View
          style={{
            position: "absolute",
            left: 24,
            top: 6,
            bottom: 6,
            width: 1,
            backgroundColor: "rgba(181,135,58,0.30)",
          }}
        />
        {phases.map((p) => (
          <View key={p.n} style={{ flexDirection: "row", marginBottom: 14, alignItems: "stretch" }}>
            <View
              style={{
                width: 48,
                alignItems: "center",
                paddingTop: 14,
              }}
            >
              <View
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 999,
                  backgroundColor: FOREST_MID,
                  borderWidth: 1,
                  borderColor: AMBER,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 12,
                    color: AMBER,
                  }}
                >
                  {p.n}
                </Text>
              </View>
            </View>
            <View
              style={{
                flex: 1,
                padding: 16,
                borderRadius: 6,
                backgroundColor: FOREST_MID,
                borderWidth: 1,
                borderColor: BORDER,
              }}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 9,
                  letterSpacing: 2.4,
                  textTransform: "uppercase",
                  color: AMBER,
                }}
              >
                Phase {p.n}
              </Text>
              <Text
                style={{
                  marginTop: 6,
                  fontFamily: FONTS.displayMedium,
                  fontSize: 20,
                  color: W,
                }}
              >
                {p.title}
              </Text>
              <Text
                style={{
                  marginTop: 4,
                  fontFamily: FONTS.body,
                  fontSize: 12.5,
                  lineHeight: 18,
                  color: WS,
                }}
              >
                {p.body}
              </Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}

// ─── What's next ───────────────────────────────────────────────────────

function WhatsNext({ audience }: { audience: ResolvedAudience }) {
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};

  if (audience.kind === "rm-waiting") {
    const p = audience.rmProgram;
    return (
      <View style={{ marginTop: 36 }}>
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 9.5,
            letterSpacing: 2,
            textTransform: "uppercase",
            color: GOLD,
          }}
        >
          {"What's next"}
        </Text>
        <Pressable
          onPress={() => router.replace(`/client/competition/${p.id}/dashboard` as Href)}
          style={[
            {
              marginTop: 12,
              padding: 22,
              borderRadius: 6,
              backgroundColor: "rgba(201,162,74,0.10)",
              borderWidth: 1.5,
              borderColor: GOLD,
            } as ViewStyle,
            cursorWeb,
          ]}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9,
              letterSpacing: 2.4,
              textTransform: "uppercase",
              color: GOLD,
            }}
          >
            Coravu Managed
          </Text>
          <Text
            style={{
              marginTop: 8,
              fontFamily: FONTS.displayMedium,
              fontSize: 22,
              color: W,
            }}
          >
            {p.name}
          </Text>
          <Text
            style={{
              marginTop: 8,
              fontFamily: FONTS.body,
              fontSize: 13,
              lineHeight: 20,
              color: WS,
            }}
          >
            {"Tyler put together the first draft of your program. Open it to review the brief, schedule, and target talent profile."}
          </Text>
          <Text
            style={{
              marginTop: 14,
              fontFamily: FONTS.bodyBold,
              fontSize: 12,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: GOLD,
            }}
          >
            {"Open program →"}
          </Text>
        </Pressable>
      </View>
    );
  }

  if (audience.kind === "fresh") {
    return (
      <View style={{ marginTop: 36 }}>
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 9.5,
            letterSpacing: 2,
            textTransform: "uppercase",
            color: AMBER,
          }}
        >
          {"What's next"}
        </Text>
        <Text
          style={{
            marginTop: 8,
            fontFamily: FONTS.display,
            fontSize: 24,
            color: LINEN,
          }}
        >
          Pick how you want to start.
        </Text>

        <View
          style={{
            marginTop: 14,
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 14,
          }}
        >
          <NextTile
            tone="rm"
            overline="Coravu Managed"
            title="Coravu runs it for me."
            body="Discovery call with Tyler scopes your program. We design and run it."
            cta="Book a call →"
            onPress={() => router.replace("/client/competition/new/book" as Href)}
          />
          <NextTile
            tone="licensing"
            overline="Self-Managed"
            title="I'll run my own program."
            body="Drop into the editor — write the brief, set deliverables, pick judges."
            cta="Start a program →"
            onPress={() => router.replace("/client/competition/new?engagement=licensing" as Href)}
          />
        </View>
      </View>
    );
  }

  // licensing-only or added-by-owner: no immediate "create" CTA, just a soft pointer.
  return (
    <View style={{ marginTop: 36 }}>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 2,
          textTransform: "uppercase",
          color: AMBER,
        }}
      >
        {"What's next"}
      </Text>
      <Text
        style={{
          marginTop: 10,
          fontFamily: FONTS.body,
          fontSize: 13.5,
          lineHeight: 22,
          color: WS,
          maxWidth: 720,
        }}
      >
        Your portal Dashboard lists every program your team is running. Click into any program to find candidates, deliverables, and the case editor.
      </Text>
    </View>
  );
}

function NextTile({
  tone,
  overline,
  title,
  body,
  cta,
  onPress,
}: {
  tone: "rm" | "licensing";
  overline: string;
  title: string;
  body: string;
  cta: string;
  onPress: () => void;
}) {
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};
  const accent = tone === "rm" ? GOLD : AMBER;
  return (
    <Pressable
      onPress={onPress}
      style={[
        {
          flexBasis: 280,
          flexGrow: 1,
          flexShrink: 1,
          padding: 20,
          borderRadius: 6,
          backgroundColor: tone === "rm" ? "rgba(201,162,74,0.06)" : "transparent",
          borderWidth: tone === "rm" ? 1.5 : 1,
          borderColor: tone === "rm" ? "rgba(201,162,74,0.55)" : BORDER,
        } as ViewStyle,
        cursorWeb,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9,
          letterSpacing: 2.4,
          textTransform: "uppercase",
          color: accent,
        }}
      >
        {overline}
      </Text>
      <Text
        style={{
          marginTop: 10,
          fontFamily: FONTS.displayMedium,
          fontSize: 22,
          color: W,
        }}
      >
        {title}
      </Text>
      <Text
        style={{
          marginTop: 8,
          fontFamily: FONTS.body,
          fontSize: 12.5,
          lineHeight: 18,
          color: WS,
        }}
      >
        {body}
      </Text>
      <Text
        style={{
          marginTop: 14,
          fontFamily: FONTS.bodyBold,
          fontSize: 12,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: accent,
        }}
      >
        {cta}
      </Text>
    </Pressable>
  );
}

// ─── Footer ────────────────────────────────────────────────────────────

function Footer({ onContinue, busy }: { onContinue: () => void; busy: boolean }) {
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};
  return (
    <View style={{ marginTop: 44, alignItems: "flex-start" }}>
      <Pressable
        onPress={onContinue}
        disabled={busy}
        style={[
          {
            paddingHorizontal: 24,
            paddingVertical: 14,
            backgroundColor: AMBER,
            borderRadius: 3,
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
          } as ViewStyle,
          cursorWeb,
        ]}
      >
        {busy ? <ActivityIndicator size="small" color={THEME.sidebarBg} /> : null}
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 12,
            letterSpacing: 1.8,
            textTransform: "uppercase",
            color: THEME.sidebarBg,
          }}
        >
          {busy ? "Loading…" : "Continue to your portal →"}
        </Text>
      </Pressable>
      <Text
        style={{
          marginTop: 12,
          fontFamily: FONTS.body,
          fontSize: 11.5,
          color: WL,
        }}
      >
        Want to revisit this tour later? It lives under Settings → Help.
      </Text>
      {/* Used to silence the unused SAGE color when audience kinds change */}
      <View style={{ display: "none" }}>
        <Text style={{ color: SAGE }}>·</Text>
      </View>
    </View>
  );
}
